import { useContext, useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import { MapContext } from "../../context/MapContext";
import { saveState } from "../../utils/SaveStateUtil";

const IndividualDrawColorSettingModal = ({ show, handleClose, id }) => {
  // コンテキストから取得
  const { drawCtl, drawColorRef, isCheckSaveState } = useContext(MapContext);
  const drawColor = drawColorRef.current;
  // State管理
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [lineColors, setLineColors] = useState({});
  const [polygonColors, setPolygonColors] = useState({});
  const [showToast, setShowToast] = useState(false);

  // 初期化処理
  useEffect(() => {
    if (!drawCtl || !id || !show) {
      setSelectedFeature(null);
      return;
    }
    // 選択している図形を取得する
    const drawInstance = drawCtl.getTerraDrawInstance();
    const snapshot = drawInstance.getSnapshot();
    const feature = snapshot?.find((feature) => feature.id === id);
    setSelectedFeature(feature);

    // 取得した図形の色を取得する
    let color;
    switch (feature.geometry.type) {
      case "LineString":
        color = drawColor[id] ?? drawColor.default.lineString;
        setLineColors({ ...color });
        break;
      case "Polygon":
        color = drawColor[id] ?? drawColor.default.polygon;
        setPolygonColors({ ...color });
        break;
      default:
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawCtl, show, id]);

  useEffect(() => {
    // チェックがONになった時、現状の既定の色を保存する
    isCheckSaveState && saveState("defaultDrawColor", drawColor.default);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCheckSaveState]);

  // 既定の色にする
  const handleSetBaseColor = () => {
    switch (selectedFeature.geometry.type) {
      case "LineString":
        drawColor.default.lineString = lineColors;
        break;
      case "Polygon":
        drawColor.default.polygon = polygonColors;
        break;
      default:
    }
    isCheckSaveState && saveState("defaultDrawColor", drawColor.default);
    setShowToast(true);
  };

  // 色の保存
  const handleSave = () => {
    switch (selectedFeature.geometry.type) {
      case "LineString":
        drawColor[id] = lineColors;
        break;
      case "Polygon":
        drawColor[id] = polygonColors;
        break;
      default:
    }
    // 図形の選択解除
    const drawInstance = drawCtl.getTerraDrawInstance();
    drawInstance.deselectFeature(id);
    // ダイアログを閉じる
    handleClose();
  };

  const viewSettings = {
    // ライン色設定
    LineString: (
      <LineSetting lineColors={lineColors} setLineColors={setLineColors} />
    ),
    // ポリゴン色設定
    Polygon: (
      <PolygonSetting
        polygonColors={polygonColors}
        setPolygonColors={setPolygonColors}
      />
    ),
  };
  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>図形色設定</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-5">
        {/* 選択図形に応じた設定画面 */}
        {selectedFeature ? viewSettings[selectedFeature.geometry.type] : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" onClick={handleClose}>
          キャンセル
        </Button>
        <div className="flex-fill" />
        <Button variant="outline-primary" onClick={handleSetBaseColor}>
          既定の設定にする
        </Button>
        <Button
          variant="primary"
          style={{ width: "100px" }}
          onClick={handleSave}
        >
          OK
        </Button>
      </Modal.Footer>
      <ToastContainer
        className="p-3"
        style={{
          position: "fixed",
          bottom: "1rem",
          right: "1rem",
        }}
      >
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">設定完了</strong>
          </Toast.Header>
          <Toast.Body>既定の色に設定しました</Toast.Body>
        </Toast>
      </ToastContainer>
    </Modal>
  );
};

export default IndividualDrawColorSettingModal;

// ライン色設定
const LineSetting = ({ lineColors, setLineColors }) => {
  return (
    <>
      <h5 className="mb-3">ライン色設定</h5>

      {/* 線色設定 */}
      <Form.Group as={Row} className="mb-3 align-items-center">
        <Form.Label column sm="6">
          線色（RGB）
        </Form.Label>
        <Col sm="6">
          <Form.Control
            type="color"
            id="lineStringColor"
            style={{ width: "72px", height: "44px" }}
            value={lineColors.lineStringColor}
            onChange={(e) =>
              setLineColors({
                ...lineColors,
                lineStringColor: e.target.value,
              })
            }
          />
        </Col>
      </Form.Group>

      {/* 線幅設定 */}
      <Form.Group as={Row} className="mb-3 align-items-center">
        <Form.Label column sm="6">
          線幅（0～100px）
        </Form.Label>
        <Col sm="6">
          <Form.Control
            type="number"
            id="lineStringWidth"
            style={{ width: "120px" }}
            min="0"
            max="100"
            value={lineColors.lineStringWidth}
            onChange={(e) =>
              setLineColors({
                ...lineColors,
                lineStringWidth: parseInt(e.target.value) || 0,
              })
            }
            onBlur={(e) => {
              const value = parseInt(e.target.value);
              if (isNaN(value) || value < 0) {
                setLineColors({
                  ...lineColors,
                  lineStringWidth: 0,
                });
              } else if (value > 100) {
                setLineColors({
                  ...lineColors,
                  lineStringWidth: 100,
                });
              }
            }}
          />
        </Col>
      </Form.Group>
    </>
  );
};

// ポリゴン色設定
const PolygonSetting = ({ polygonColors, setPolygonColors }) => {
  return (
    <>
      <h5 className="mb-3">ポリゴン色設定</h5>

      {/* 塗りつぶしの色設定 */}
      <Form.Group as={Row} className="mb-3 align-items-center">
        <Form.Label column sm="6">
          塗りつぶしの色（RGB）
        </Form.Label>
        <Col sm="6">
          <Form.Control
            type="color"
            id="polygonFillColor"
            style={{ width: "72px", height: "44px" }}
            value={polygonColors.fillColor}
            onChange={(e) =>
              setPolygonColors({
                ...polygonColors,
                fillColor: e.target.value,
              })
            }
          />
        </Col>
      </Form.Group>

      {/* 不透明度設定 */}
      <Form.Group as={Row} className="mb-3 align-items-center">
        <Form.Label column sm="6">
          不透明度（0～1）
        </Form.Label>
        <Col sm="6">
          <Form.Control
            type="number"
            id="polygonFillOpacity"
            style={{ width: "120px" }}
            step="0.1"
            min="0"
            max="1"
            value={polygonColors.fillOpacity}
            onChange={(e) =>
              setPolygonColors({
                ...polygonColors,
                fillOpacity: parseFloat(e.target.value) || 0,
              })
            }
            onBlur={(e) => {
              const value = parseFloat(e.target.value);
              if (isNaN(value) || value < 0) {
                setPolygonColors({
                  ...polygonColors,
                  fillOpacity: 0,
                });
              } else if (value > 1) {
                setPolygonColors({
                  ...polygonColors,
                  fillOpacity: 1,
                });
              }
            }}
          />
        </Col>
      </Form.Group>

      {/* 輪郭線色設定 */}
      <Form.Group as={Row} className="mb-3 align-items-center">
        <Form.Label column sm="6">
          輪郭線色（RGB）
        </Form.Label>
        <Col sm="6">
          <Form.Control
            type="color"
            id="polygonOutlineColor"
            style={{ width: "72px", height: "44px" }}
            value={polygonColors.outlineColor}
            onChange={(e) =>
              setPolygonColors({
                ...polygonColors,
                outlineColor: e.target.value,
              })
            }
          />
        </Col>
      </Form.Group>

      {/* 輪郭線の線幅設定 */}
      <Form.Group as={Row} className="mb-3 align-items-center">
        <Form.Label column sm="6">
          輪郭線の線幅（0～100px）
        </Form.Label>
        <Col sm="6">
          <Form.Control
            type="number"
            id="polygonOutlineWidth"
            style={{ width: "120px" }}
            min="0"
            max="100"
            value={polygonColors.outlineWidth}
            onChange={(e) =>
              setPolygonColors({
                ...polygonColors,
                outlineWidth: parseInt(e.target.value) || 0,
              })
            }
            onBlur={(e) => {
              const value = parseInt(e.target.value);
              if (isNaN(value) || value < 0) {
                setPolygonColors({
                  ...polygonColors,
                  outlineWidth: 0,
                });
              } else if (value > 100) {
                setPolygonColors({
                  ...polygonColors,
                  outlineWidth: 100,
                });
              }
            }}
          />
        </Col>
      </Form.Group>
    </>
  );
};
